import React from 'react';
import { useTranslation } from 'react-i18next';
import QuranLoading from '../app/quran/QuranLoading';

/**
 * GlobalLoadingScreen
 * High-performance, aesthetic full-screen loading screen with dual-ring spinning animation,
 * pulsing brand emblem, and multilingual status labels.
 * Automatically adapts to light and dark theme mode.
 */
export default function GlobalLoadingScreen({ message, subMessage }) {
  const { i18n } = useTranslation();
  const isAr = i18n.language ? i18n.language.startsWith('ar') : true;

  const isQuranRoute = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/quran') || window.location.pathname.includes('/app/quran')
  );

  if (isQuranRoute) {
    return (
      <div className="fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-white dark:bg-[#171614] select-none">
        <QuranLoading
          layout="lesson"
          isAr={isAr}
          title={message || (isAr ? 'جاري تهيئة المحتوى القرآني...' : 'Preparing Quranic content...')}
          message={subMessage || (isAr ? 'لحظات ونبدأ في رحاب العلم والمعرفة' : 'Preparing verified learning materials...')}
        />
      </div>
    );
  }

  const defaultTitle = isAr ? 'جاري تهيئة المنصة...' : 'Loading application...';
  const defaultSubtitle = isAr ? 'يرجى الانتظار' : 'Please wait';

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-slate-50 dark:bg-[#090d16] text-slate-900 dark:text-slate-100 font-sans select-none transition-colors duration-200"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="flex flex-col items-center justify-center gap-5 p-6 max-w-[90vw] text-center">
        {/* Animated Dual-Ring Spinner Box */}
        <div className="relative w-20 h-20 sm:w-22 sm:h-22 flex items-center justify-center">
          {/* Static Track */}
          <div className="absolute inset-0 rounded-full border-[3.5px] border-primary-accent/15 dark:border-white/10" />

          {/* Rotating Glowing Gradient Ring */}
          <div className="absolute inset-0 rounded-full border-[3.5px] border-transparent border-t-primary-accent border-r-sky-400 animate-spin filter drop-shadow-[0_0_12px_rgba(59,130,246,0.4)]" />

          {/* Reverse-Spinning Dashed Inner Ring */}
          <div
            className="absolute inset-2 rounded-full border-2 border-dashed border-sky-400/40 dark:border-sky-300/30"
            style={{ animation: 'spin 3s linear infinite reverse' }}
          />

          {/* Central Pulsing Brand Logo */}
          <div className="relative z-10 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center animate-pulse drop-shadow-md">
            <svg width="36" height="36" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g transform="rotate(45, 256, 256)">
                <rect x="80" y="210" width="352" height="92" rx="46" fill="#3B5BFF" />
                <path d="M80 256C80 230.595 100.595 210 126 210H170V302H126C100.595 302 80 281.405 80 256Z" fill="#FFD600" />
                <path d="M432 256C432 281.405 411.405 302 386 302H350V210H386C411.405 210 432 230.595 432 256Z" fill="#94A6FF" />
              </g>
              <g transform="rotate(-45, 256, 256)">
                <path d="M280 210H386C411.405 210 432 230.595 432 256C432 281.405 411.405 302 386 302H330V210H280Z" fill="#3B5BFF" />
                <path d="M432 256C432 230.595 411.405 210 386 210H330V302H386C411.405 302 432 281.405 432 256Z" fill="#FFD600" />
                <path d="M330 210H300V302H330V210Z" fill="#94A6FF" />
                <path d="M80 256C80 230.595 100.595 210 126 210H232V302H126C100.595 302 80 281.405 80 256Z" fill="#3B5BFF" />
                <path d="M80 256C80 281.405 100.595 302 126 302H180V210H126C100.595 210 80 230.595 80 256Z" fill="#FFD600" />
              </g>
            </svg>
          </div>
        </div>

        {/* Labels & Animated Dots */}
        <div className="flex flex-col items-center gap-1.5">
          <span className="text-sm sm:text-base font-bold tracking-tight text-slate-800 dark:text-slate-100">
            {message || defaultTitle}
          </span>
          <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
            <span>{subMessage || defaultSubtitle}</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-accent animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-primary-accent animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-primary-accent animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
